import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import api from "@/lib/api";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

function getSession() {
  let s = localStorage.getItem("vf_ask_session");
  if (!s) { s = crypto.randomUUID(); localStorage.setItem("vf_ask_session", s); }
  return s;
}

function firstTouch() {
  let a = {};
  try { a = JSON.parse(localStorage.getItem("vf_attr") || "{}"); } catch (_) {}
  if (!a.landing_page) {
    const p = new URLSearchParams(window.location.search);
    a = {
      utm_source: p.get("utm_source") || undefined,
      utm_medium: p.get("utm_medium") || undefined,
      utm_campaign: p.get("utm_campaign") || undefined,
      utm_content: p.get("utm_content") || undefined,
      referrer: document.referrer || undefined,
      landing_page: window.location.pathname + window.location.search,
    };
    Object.keys(a).forEach((k) => a[k] === undefined && delete a[k]);
    localStorage.setItem("vf_attr", JSON.stringify(a));
  }
  return a;
}

function buildContext(pathname) {
  const ctx = { ...firstTouch(), source: pathname, current_page: pathname, session_id: getSession() };
  const vm = pathname.match(/^\/video\/([^/]+)/);
  if (vm) ctx.video_slug = vm[1];
  const cm = pathname.match(/^\/creators\/([^/]+)/);
  if (cm) ctx.creator_slug = cm[1];
  return ctx;
}

export default function AskVytali() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const session = useRef(getSession());
  const bottomRef = useRef(null);

  // hide on admin/auth screens
  const hidden = /^\/(admin|login|register|forgot-password|reset-password|set-password|verify-email)/.test(location.pathname);

  useEffect(() => {
    if (open && msgs.length === 0) {
      setMsgs([{ role: "assistant", content: "Bonjour 👋 Je suis Ask Vytali. Une question sur une vidéo, un partenaire ou l'écosystème Vytali ? Je suis là pour vous aider." }]);
    }
  }, [open]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  if (hidden) return null;

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", content: text }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ask-vytali/message", {
        session_id: session.current, message: text, context: buildContext(location.pathname),
      });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (_) {
      setMsgs((m) => [...m, { role: "assistant", content: "Désolé, une erreur est survenue. Réessayez dans un instant." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="ask-vytali-button"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-primary px-5 py-3.5 font-semibold text-primary-foreground shadow-2xl shadow-primary/30 transition-transform hover:scale-105">
          <Sparkles className="h-5 w-5" /> Ask Vytali
        </button>
      )}

      {open && (
        <div data-testid="ask-vytali-panel" className="fixed bottom-6 right-6 z-50 flex h-[560px] max-h-[80vh] w-[calc(100vw-3rem)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[hsl(var(--surface))] shadow-2xl">
          <div className="flex items-center justify-between border-b border-white/10 bg-gradient-to-r from-primary/20 to-transparent px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground"><Sparkles className="h-4 w-4" /></span>
              <div>
                <p className="font-heading text-sm font-bold leading-tight">Ask Vytali</p>
                <p className="text-[11px] text-muted-foreground">Assistant VytaliFlix</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ask-vytali-close" className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-white/5 text-foreground"}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="rounded-2xl bg-white/5 px-3.5 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div></div>}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={send} className="border-t border-white/10 p-3">
            <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 focus-within:border-primary/50">
              <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="ask-vytali-input"
                placeholder="Écrivez votre message…" className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
              <button type="submit" data-testid="ask-vytali-send" disabled={loading || !input.trim()} className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-40">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
